import * as React from "react";

import { DocShape } from "@eave/shared/types/api";
import { docToUrl, respToContentType } from "./Documents/helpers";

/* helpers */

const contentTypeIsViewableImage = (ct: string) =>
  ["image/gif", "image/jpeg", "image/png"].includes(ct);

const contentTypeIsViewableObject = (ct: string) =>
  ["application/pdf", "text/csv", "text/plain"].includes(ct);

/* DocRender */

interface IDocRender {
  doc?: DocShape;
  onFileObjectURLUpdate?: Function;
}

class DocRender extends React.Component<IDocRender, any> {
  constructor(props) {
    super(props);
    this.state = DocRender.initialState();
  }

  static initialState() {
    return {
      error: null,
      contentType: null,
      fileBlob: null,
    };
  }

  componentDidMount() {
    if (this.props.doc) {
      this.fetchDocData(this.props.doc);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.doc) {
      return;
    }
    if (this.props.doc && nextProps.doc.id === this.props.doc.id) {
      return;
    }
    this.setState(DocRender.initialState(), () => {
      if (nextProps.doc) {
        this.fetchDocData(nextProps.doc);
      }
    });
  }

  shouldComponentUpdate(nextProps, nextState) {
    return nextState.error !== this.state.error || nextState.fileBlob !== this.state.fileBlob;
  }

  setError(error) {
    this.setState({
      ...this.state,
      error,
    });
  }

  fetchDocData(doc) {
    fetch(docToUrl(doc), { credentials: "same-origin" })
      .then((resp: Response) => {
        if (!resp.ok) {
          this.setError(`${resp.status}: ${resp.statusText}`);
          return;
        }

        resp
          .blob()
          .then((blob: Blob) => {
            this.setState({
              contentType: respToContentType(resp),
              fileBlob: blob,
            });
            const url = URL.createObjectURL(blob);
            if (this.props.onFileObjectURLUpdate) {
              this.props.onFileObjectURLUpdate(url);
            }
          })
          .catch(() => {
            this.setError("Couldn't get document blob.");
          });
      })
      .catch(() => {
        this.setError("Couldn't show document because of a network issue.");
      });
  }

  renderContent() {
    const { contentType, fileBlob } = this.state;

    if (contentTypeIsViewableImage(contentType)) {
      return (
        <div style={{ width: "100%", height: "100%", overflowY: "scroll" }}>
          <img src={URL.createObjectURL(fileBlob)} />
        </div>
      );
    }
    if (contentTypeIsViewableObject(contentType)) {
      return <object data={URL.createObjectURL(fileBlob)} width="100%" height="100%" />;
    }

    return (
      <div style={{ marginTop: 10 }}>
        This document can't be viewed inline. Please download to open.
      </div>
    );
  }

  render() {
    const { error, contentType, fileBlob } = this.state;

    if (!this.props.doc) {
      return null;
    }
    if (error) {
      return <div>An error occured: {error}</div>;
    }
    if (!contentType || !fileBlob) {
      return null; // loading
    }

    return this.renderContent();
  }
}

export default DocRender;
